import React from 'react';

import {
  IFacialRecognition,
  IFacialRecognitionProcess,
} from 'shared/domain/entities';

import {
  FailedStatus,
  OnboardingLink,
  PendingStatus,
  RecognitionError,
  RecognitionLoading,
  Setup,
} from './components';
import { useRecognitionStatus } from './hooks';

interface IFacialRecognitionProps {
  process: IFacialRecognitionProcess;
  onRecognitionSuccess(recognition: IFacialRecognition): void;
}

const FacialRecognition: React.FC<IFacialRecognitionProps> = ({
  process,
  onRecognitionSuccess,
}) => {
  const controller = useRecognitionStatus({
    process,
    onSuccess: onRecognitionSuccess,
  });

  if (controller.loading) return <RecognitionLoading />;

  if (controller.error) {
    return <RecognitionError error={controller.error.message} />;
  }

  if (
    !controller.selectedOnboarding &&
    controller.recognition?.availableOnboardings.length
  ) {
    return (
      <Setup
        process={process}
        availableOnboardings={controller.recognition.availableOnboardings}
        onGoToOnboarding={controller.reset}
      />
    );
  }

  if (controller.recognition?.state.type === 'ONBOARDING') {
    return (
      <OnboardingLink
        process={process}
        link={controller.recognition.state.url}
        onOnboardingFinished={controller.refetch}
      />
    );
  }

  if (controller.recognition?.state.type === 'PENDING') {
    return <PendingStatus />;
  }

  if (controller.recognition?.state.type === 'FAILED') {
    return (
      <FailedStatus
        activeOnboarding={controller.recognition.onboarding}
        availableOnboardings={controller.recognition.availableOnboardings}
        state={controller.recognition.state}
        onGoToOnboarding={controller.reset}
      />
    );
  }

  return null;
};

export default FacialRecognition;
